import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import addInvoiceApi from '../../../../../../middleware/api/ProjectManagement/Projects/ProjectView/Invoices/AddInvoice/addInvoiceApi'
import {
  AddInvoiceProps,
  AddInvoiceSliceState,
  ChangeRequestNumbers,
  GetNumbersAndPercentageBillableResponse,
  GetNumbersAndPercentageData,
  GetNumbersAndPercentageForChangeRequestNumbers,
  GetNumbersAndPercentageForProjectNumbers,
  GetNumbersAndPercentageResponse,
  GetNumbersAndPercentageResponseProps,
  InvoiceEdit,
  InvoiceNumber,
  InvoiceSerialNumber,
  InvoiceStatusHistory,
  LookUp,
  MilestoneBillableList,
  ProjectData,
  ProjectNumbersForFixedBid,
  Props,
  WireTransferDetails,
  getByRolePayload,
} from '../../../../../../types/ProjectManagement/Project/ProjectView/Invoices/AddInvoice/addInvoiceTypes'
import {
  LoadingState,
  ValidationError,
} from '../../../../../../types/commonTypes'
import { ApiLoadingState } from '../../../../../../middleware/api/apiList'
import { RootState } from '../../../../../../stateStore'
import { UpdateInvoice } from '../../../../../../types/ProjectManagement/Project/ProjectView/Invoices/AddInvoice/editInvoiceTypes'
import { MilestoneList } from '../../../../../../types/ProjectManagement/Project/ProjectView/Invoices/invoicesTypes'

const getNumbersAndPercentage = createAsyncThunk(
  'addInvoice/getNumbersAndPercentage',
  async (props: GetNumbersAndPercentageResponseProps, thunkApi) => {
    try {
      return await addInvoiceApi.getNumbersAndPercentage(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getNumbersAndPercentageBillable = createAsyncThunk(
  'addInvoice/getNumbersAndPercentageBillable',
  async (props: GetNumbersAndPercentageResponseProps, thunkApi) => {
    try {
      return await addInvoiceApi.getNumbersAndPercentageBillable(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addInvoiceDetails = createAsyncThunk(
  'addInvoice/addInvoiceDetails',
  async (props: AddInvoiceProps, thunkApi) => {
    try {
      return await addInvoiceApi.addInvoiceDetails(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const allowToAdjustInvoice = createAsyncThunk(
  'addInvoice/allowToAdjustInvoice',
  async (invoiceId: number, thunkApi) => {
    try {
      return await addInvoiceApi.allowToAdjustInvoice(invoiceId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const restrictToAdjustInvoice = createAsyncThunk(
  'addInvoice/restrictToAdjustInvoice',
  async (invoiceId: number, thunkApi) => {
    try {
      return await addInvoiceApi.restrictToAdjustInvoice(invoiceId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const projectData = createAsyncThunk(
  'addInvoice/projectData',
  async (projectId: number, thunkApi) => {
    try {
      return await addInvoiceApi.projectData(projectId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const lookup = createAsyncThunk(
  'addInvoiceForm/lookup',
  async (_, thunkApi) => {
    try {
      return await addInvoiceApi.lookup()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const clientInvoiceCountry = createAsyncThunk(
  'addInvoiceForm/clientInvoiceCountry',
  async (
    { country, clientName }: { country: string; clientName: string },
    thunkApi,
  ) => {
    try {
      return await addInvoiceApi.clientInvoiceCountry(country, clientName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getInvoiceSerialNumber = createAsyncThunk(
  'addInvoiceForm/getInvoiceSerialNumber',
  async (item: Props, thunkApi) => {
    try {
      return await addInvoiceApi.getInvoiceSerialNumber(item)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const invoiceEdit = createAsyncThunk(
  'addInvoiceForm/invoiceEdit',
  async (id: number, thunkApi) => {
    try {
      return await addInvoiceApi.invoiceEdit(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getInvoiceDetails = createAsyncThunk(
  'addInvoiceForm/getInvoiceDetails',
  async (invoiceId: number, thunkApi) => {
    try {
      return await addInvoiceApi.getInvoiceDetails(invoiceId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getNumbersAndPercentageForProjectNumbers = createAsyncThunk(
  'addInvoice/getNumbersAndPercentageForProjectNumbers',
  async (props: GetNumbersAndPercentageResponseProps, thunkApi) => {
    try {
      return await addInvoiceApi.getNumbersAndPercentageForProjectNumbers(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getNumbersAndPercentageForChangeRequestNumbers = createAsyncThunk(
  'addInvoice/getNumbersAndPercentageForChangeRequestNumbers',
  async (props: GetNumbersAndPercentageResponseProps, thunkApi) => {
    try {
      return await addInvoiceApi.getNumbersAndPercentageForChangeRequestNumbers(
        props,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const invoiceCountryBaseApi = createAsyncThunk(
  'addInvoiceForm/invoiceCountryBaseApi',
  async (
    {
      client,
      serialNumber,
      country,
    }: { client: string; serialNumber: string; country: string },
    thunkApi,
  ) => {
    try {
      return await addInvoiceApi.invoiceCountryBaseApi(
        client,
        serialNumber,
        country,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getInvoiceStatusHistoryById = createAsyncThunk(
  'addInvoice/getInvoiceStatusHistoryById',
  async (invoiceId: number, thunkApi) => {
    try {
      return await addInvoiceApi.getInvoiceStatusHistoryById(invoiceId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteInvoiceId = createAsyncThunk(
  'addInvoice/deleteInvoiceId',
  async (id: number, thunkApi) => {
    try {
      return await addInvoiceApi.deleteInvoiceId(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getByRole = createAsyncThunk(
  'addInvoice/getByRole',
  async (props: getByRolePayload, thunkApi) => {
    try {
      return await addInvoiceApi.getByRole(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateInvoice = createAsyncThunk(
  'addInvoice/updateInvoice',
  async (props: UpdateInvoice, thunkApi) => {
    try {
      return await addInvoiceApi.updateInvoice(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const checkInvoiceNumber = createAsyncThunk(
  'addInvoiceForm/checkInvoiceNumber',
  async (
    {
      client,
      serialNumber,
      country,
    }: { client: string; serialNumber: number | string; country: string },
    thunkApi,
  ) => {
    try {
      return await addInvoiceApi.checkInvoiceNumber(
        client,
        serialNumber,
        country,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const checkSerialNumber = createAsyncThunk(
  'addInvoiceForm/checkSerialNumber',
  async (
    {
      serialNumber,
      country,
      invoiceType,
    }: {
      serialNumber: number | string
      country: string
      invoiceType: string | undefined
    },
    thunkApi,
  ) => {
    try {
      return await addInvoiceApi.checkSerialNumber(
        serialNumber,
        country,
        invoiceType,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getWireTransferInstructions = createAsyncThunk(
  'addInvoiceForm/getWireTransferInstructions',
  async (
    { currency, bankName }: { currency: string; bankName: string },
    thunkApi,
  ) => {
    try {
      return await addInvoiceApi.getWireTransferInstructions(currency, bankName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deletePartialReceivedAmount = createAsyncThunk(
  'addInvoice/deleteInvoiceId',
  async (
    {
      amountId,
      invoiceId,
    }: {
      amountId: number
      invoiceId: number
    },
    thunkApi,
  ) => {
    try {
      return await addInvoiceApi.deletePartialReceivedAmount(
        amountId,
        invoiceId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const checkMilestoneExits = createAsyncThunk(
  'addInvoice/checkMilestoneExits',
  async (milestoneid: number, thunkApi) => {
    try {
      return await addInvoiceApi.checkMilestoneExits(milestoneid)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const invoiceMileStones = createAsyncThunk(
  'addInvoice/invoiceMileStones',
  async (projectId: number, thunkApi) => {
    try {
      return await addInvoiceApi.invoiceMileStones(projectId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const raisedCRPercentage = createAsyncThunk(
  'addInvoice/raisedCRPercentage',
  async (crId: number, thunkApi) => {
    try {
      return await addInvoiceApi.raisedCRPercentage(crId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialAddInvoiceState: AddInvoiceSliceState = {
  milestoneBillableList: [],
  milestoneBillableResources: [],
  getNumbersAndPercentageResponse: {} as GetNumbersAndPercentageResponse,
  isLoading: ApiLoadingState.idle,
  invoiceProjectData: {} as ProjectData,
  lookUpList: {} as LookUp,
  invoiceSerialNumber: {} as InvoiceSerialNumber,
  invoiceNumber: {} as InvoiceNumber,
  invoiceEditInfo: {} as InvoiceEdit,
  getNumbersAndPercentageForProjectNumbers:
    {} as GetNumbersAndPercentageForProjectNumbers,
  getNumbersAndPercentageForChangeRequestNumbers:
    {} as GetNumbersAndPercentageForChangeRequestNumbers,
  projectNumbers: {} as ProjectNumbersForFixedBid,
  changeRequestNumbers: {} as ChangeRequestNumbers,
  InvoiceStatusHistory: [],
  getNumbersAndPercentageBillableResponse:
    {} as GetNumbersAndPercentageBillableResponse,
  getByRoleAPIResponse: [],
  wireTransferDetails: [],
  value: null,
  milestoneId: null,
  raiseButton: false,
  currentMilestone: {} as MilestoneList,
  currentMilestonePercentage: 0,
  currentChangeRequestPercentage: 0,
  numbersAndPercentageData: {} as GetNumbersAndPercentageData,
  adjustInvoiceGlobal: false,
  stateInvoiceGlobal: 'RAISED',
  isUpdate: false,
  isGo: false,

  pendingApiCalls: 0, // this is used to track the number of pending API calls in the slice
}
const addInvoiceSlice = createSlice({
  name: 'addInvoice',
  initialState: initialAddInvoiceState,
  reducers: {
    setMilestoneId: (state, action) => {
      state.milestoneId = action.payload
    },
    setRaiseButton: (state, action) => {
      state.raiseButton = action.payload
    },
    setCurrentMilestone: (state, action) => {
      state.currentMilestone = action.payload
    },
    setAdJustInvoiceGlobal: (state, action) => {
      state.adjustInvoiceGlobal = action.payload
    },
    setInvoiceStatusGlobal: (state, action) => {
      state.stateInvoiceGlobal = action.payload
    },
    setIsUpdate: (state, action) => {
      state.isUpdate = action.payload
    },
    setIsGo: (state, action) => {
      state.isGo = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNumbersAndPercentage.fulfilled, (state, action) => {
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
        state.milestoneBillableList = action.payload.milestonebillableList
        state.numbersAndPercentageData = {
          percentage: action.payload.percentage || 0,
          data:
            action.payload.projectNumbers ||
            action.payload.changeRequestNumbers ||
            null,
        }
        state.getNumbersAndPercentageForProjectNumbers = {
          percentage: action.payload.percentage || 0,
          projectNumbers: action.payload.projectNumbers,
        }
        state.getNumbersAndPercentageForChangeRequestNumbers = {
          percentage: action.payload.percentage || 0,
          changeRequestNumbers: action.payload.changeRequestNumbers,
        }
      })
      .addCase(getNumbersAndPercentageBillable.fulfilled, (state, action) => {
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
        state.getNumbersAndPercentageBillableResponse = action.payload
      })
      .addCase(
        getNumbersAndPercentageForProjectNumbers.fulfilled,
        (state, action) => {
          state.pendingApiCalls--
          if (state.pendingApiCalls === 0) {
            state.isLoading = ApiLoadingState.succeeded
          }
          state.getNumbersAndPercentageForProjectNumbers = action.payload
          state.numbersAndPercentageData = {
            percentage: action.payload.percentage || 0,
            data: action.payload.projectNumbers || null,
          }
        },
      )
      .addCase(
        getNumbersAndPercentageForChangeRequestNumbers.fulfilled,
        (state, action) => {
          state.pendingApiCalls--
          if (state.pendingApiCalls === 0) {
            state.isLoading = ApiLoadingState.succeeded
          }
          state.getNumbersAndPercentageForChangeRequestNumbers = action.payload
          state.numbersAndPercentageData = {
            percentage: action.payload.percentage || 0,
            data: action.payload.changeRequestNumbers || null,
          }
        },
      )
      .addCase(projectData.fulfilled, (state, action) => {
        state.invoiceProjectData = action.payload
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      .addCase(lookup.fulfilled, (state, action) => {
        state.lookUpList = action.payload
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      .addCase(invoiceCountryBaseApi.fulfilled, (state) => {
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      .addCase(checkMilestoneExits.fulfilled, (state, action) => {
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
        state.value = action.payload as boolean
      })
      .addCase(invoiceMileStones.fulfilled, (state, action) => {
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
        state.currentMilestonePercentage = action.payload
      })
      .addCase(raisedCRPercentage.fulfilled, (state, action) => {
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
        state.currentChangeRequestPercentage = action.payload
      })
      .addCase(clientInvoiceCountry.fulfilled, (state, action) => {
        state.invoiceSerialNumber = action.payload
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      .addCase(getInvoiceSerialNumber.fulfilled, (state, action) => {
        state.invoiceNumber = action.payload
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      .addCase(invoiceEdit.fulfilled, (state, action) => {
        state.invoiceEditInfo = action.payload
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      .addCase(getInvoiceStatusHistoryById.fulfilled, (state, action) => {
        state.InvoiceStatusHistory = action.payload
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      .addCase(getByRole.fulfilled, (state, action) => {
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
        state.getByRoleAPIResponse = action.payload
      })
      .addCase(getWireTransferInstructions.fulfilled, (state, action) => {
        state.wireTransferDetails = action.payload
        state.pendingApiCalls--
        if (state.pendingApiCalls === 0) {
          state.isLoading = ApiLoadingState.succeeded
        }
      })
      // ======================================================= //
      .addCase(projectData.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(getNumbersAndPercentage.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(getNumbersAndPercentageBillable.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(getNumbersAndPercentageForProjectNumbers.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(
        getNumbersAndPercentageForChangeRequestNumbers.pending,
        (state) => {
          state.pendingApiCalls++
        },
      )
      .addCase(lookup.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(invoiceCountryBaseApi.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(checkMilestoneExits.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(invoiceMileStones.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(raisedCRPercentage.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(clientInvoiceCountry.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(getInvoiceSerialNumber.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(invoiceEdit.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(getInvoiceStatusHistoryById.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(getByRole.pending, (state) => {
        state.pendingApiCalls++
      })
      .addCase(getWireTransferInstructions.pending, (state) => {
        state.pendingApiCalls++
      })
      // ======================================================= //
      .addCase(projectData.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(getNumbersAndPercentage.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(getNumbersAndPercentageBillable.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(getNumbersAndPercentageForProjectNumbers.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(
        getNumbersAndPercentageForChangeRequestNumbers.rejected,
        (state) => {
          state.pendingApiCalls--
        },
      )
      .addCase(lookup.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(invoiceCountryBaseApi.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(checkMilestoneExits.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(invoiceMileStones.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(raisedCRPercentage.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(clientInvoiceCountry.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(getInvoiceSerialNumber.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(invoiceEdit.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(getInvoiceStatusHistoryById.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(getByRole.rejected, (state) => {
        state.pendingApiCalls--
      })
      .addCase(getWireTransferInstructions.rejected, (state) => {
        state.pendingApiCalls--
      })
      // ADD HERE THE API YOU WANT TO TRIGGER LOADER COMPONENT
      //! WARNING: IF YOU ADD API WITHOUT LOGIC, IT WILL TRIGGER ENDLESS LOOP FOR THE LOADER COMPONENT
      .addMatcher(isAnyOf(invoiceEdit.pending), (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addMatcher(isAnyOf(invoiceEdit.rejected), (state) => {
        state.isLoading = ApiLoadingState.failed
      })
  },
})

const addInvoiceThunk = {
  getNumbersAndPercentage,
  getNumbersAndPercentageBillable,
  addInvoiceDetails,
  allowToAdjustInvoice,
  restrictToAdjustInvoice,
  projectData,
  lookup,
  clientInvoiceCountry,
  getInvoiceSerialNumber,
  invoiceEdit,
  getNumbersAndPercentageForProjectNumbers,
  getNumbersAndPercentageForChangeRequestNumbers,
  invoiceCountryBaseApi,
  getInvoiceStatusHistoryById,
  deleteInvoiceId,
  getByRole,
  updateInvoice,
  checkInvoiceNumber,
  getWireTransferInstructions,
  deletePartialReceivedAmount,
  checkSerialNumber,
  checkMilestoneExits,
  invoiceMileStones,
  raisedCRPercentage,
  getInvoiceDetails,
}

const isLoading = (state: RootState): LoadingState => state.addInvoice.isLoading

const logPendingApiCalls = (state: RootState): number =>
  state.addInvoice.pendingApiCalls

const milestoneBillableList = (state: RootState): MilestoneBillableList[] =>
  state.addInvoice.milestoneBillableList

const milestoneBillableResources = (
  state: RootState,
): MilestoneBillableList[] => state.addInvoice.milestoneBillableResources

const getNumbersAndPercentageData = (
  state: RootState,
): GetNumbersAndPercentageData => state.addInvoice.numbersAndPercentageData

const getNumbersAndPercentageForProjectNumbersSelector = (
  state: RootState,
): GetNumbersAndPercentageForProjectNumbers =>
  state.addInvoice.getNumbersAndPercentageForProjectNumbers

const getNumbersAndPercentageForChangeRequestNumbersSelector = (
  state: RootState,
): GetNumbersAndPercentageForChangeRequestNumbers =>
  state.addInvoice.getNumbersAndPercentageForChangeRequestNumbers

const getNumbersAndPercentageEdit = (
  state: RootState,
): GetNumbersAndPercentageBillableResponse =>
  state.addInvoice.getNumbersAndPercentageBillableResponse

const changeRequestNumbersResponse = (state: RootState): ChangeRequestNumbers =>
  state.addInvoice.changeRequestNumbers

const projectNumbersResponse = (state: RootState): ProjectNumbersForFixedBid =>
  state.addInvoice.projectNumbers

const invoiceProjectData = (state: RootState): ProjectData =>
  state.addInvoice.invoiceProjectData

const LookUpData = (state: RootState): LookUp => state.addInvoice.lookUpList

const invoiceNumberDetails = (state: RootState): InvoiceNumber =>
  state.addInvoice.invoiceNumber

const serialNumberDetails = (state: RootState): InvoiceSerialNumber =>
  state.addInvoice.invoiceSerialNumber

const invoiceEditDetails = (state: RootState): InvoiceEdit =>
  state.addInvoice.invoiceEditInfo

const invoiceStatusHistory = (state: RootState): InvoiceStatusHistory[] =>
  state.addInvoice.InvoiceStatusHistory

const wireTransferDetails = (state: RootState): WireTransferDetails[] =>
  state.addInvoice.wireTransferDetails

const milestoneExist = (state: RootState): boolean | null =>
  state.addInvoice.value

const getMilestoneId = (state: RootState): number | null =>
  state.addInvoice.milestoneId

const getRaiseButton = (state: RootState): boolean =>
  state.addInvoice.raiseButton

const getCurrentMilestone = (state: RootState): MilestoneList =>
  state.addInvoice.currentMilestone

const getCurrentMilestonePercentage = (state: RootState): number =>
  state.addInvoice.currentMilestonePercentage
const getCurrentChangeRequestPercentage = (state: RootState): number =>
  state.addInvoice.currentChangeRequestPercentage
const getAdjustInvoiceGlobal = (state: RootState): boolean =>
  state.addInvoice.adjustInvoiceGlobal

const getInvoiceStatusGlobal = (state: RootState): string =>
  state.addInvoice.stateInvoiceGlobal

const getIsUpdate = (state: RootState): boolean => state.addInvoice.isUpdate

const getIsGo = (state: RootState): boolean => state.addInvoice.isGo

const addInvoiceSelectors = {
  isLoading,
  logPendingApiCalls,
  milestoneBillableList,
  milestoneBillableResources,
  getNumbersAndPercentageEdit,
  invoiceProjectData,
  LookUpData,
  invoiceNumberDetails,
  serialNumberDetails,
  invoiceEditDetails,
  getNumbersAndPercentageForProjectNumbersSelector,
  getNumbersAndPercentageForChangeRequestNumbersSelector,
  changeRequestNumbersResponse,
  projectNumbersResponse,
  invoiceStatusHistory,
  wireTransferDetails,
  milestoneExist,
  getMilestoneId,
  getRaiseButton,
  getCurrentMilestone,
  getCurrentMilestonePercentage,
  getCurrentChangeRequestPercentage,
  getNumbersAndPercentageData,
  getAdjustInvoiceGlobal,
  getInvoiceStatusGlobal,
  getIsUpdate,
  getIsGo,
}

export const addInvoiceServices = {
  ...addInvoiceThunk,
  actions: addInvoiceSlice.actions,
  selectors: addInvoiceSelectors,
}
export default addInvoiceSlice.reducer
